import { Withdrawal } from "@apis/index";
import Button from "@components/button";
import Row from "@components/row";
import Table from "@components/table";
import Title from "@components/title";
import { useModal } from "@context/ModalContext";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "@emotion/styled";
import {
  Clock,
  CheckCircle2,
  XCircle,
  User,
  Phone,
  BadgeCheck,
  Eye,
  History,
  Calendar,
} from "lucide-react";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import WithdrawalStats from "./components/WithdrawalStats";

const PayoutHistoryModal = ({
  item,
  onClose,
  settings,
}: {
  item: any;
  onClose: () => void;
  settings: any;
}) => {
  const { bucketUrl } = useConfig();
  const { showModal, hideModal } = useModal();
  const payouts = item.payouts || [];

  return (
    <ModalOverlay>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <ModalTitle style={{ marginBottom: 0 }}>Payout History</ModalTitle>
        <div
          style={{
            background: "#f3f4f6",
            padding: "4px 12px",
            borderRadius: "99px",
            fontSize: "12px",
            color: "#6b7280",
            fontWeight: 500,
          }}
        >
          #{item.id}
        </div>
      </div>

      <ModalSubtitle>
        Detailed breakdown of all payments made for this request.
      </ModalSubtitle>

      <div
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          paddingRight: "8px",
          marginTop: "20px",
        }}
      >
        {payouts.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#9ca3af",
            }}
          >
            No payouts recorded yet.
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {payouts.map((payout: any, index: number) => (
              <div
                key={payout.id}
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  style={{ display: "flex", gap: "16px", alignItems: "center" }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: "#ecfdf5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#10b981",
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircle2 size={20} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#111827",
                        fontSize: "15px",
                      }}
                    >
                      {Helper.formatCurrency(payout.amount, settings)}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {new Date(payout.createdAt).toLocaleString("en-US", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>

                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  {payout.receipt && (
                    <div
                      onClick={() =>
                        showModal(
                          <ViewReceiptModal
                            receipt={payout.receipt}
                            onClose={hideModal}
                          />,
                        )
                      }
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        border: "1px solid #e5e7eb",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={`${bucketUrl}/${payout.receipt}`}
                        alt="Receipt"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        fontFamily: "monospace",
                      }}
                    >
                      {payout.transactionId || "No TXID"}
                    </div>
                    {payout.adminNote && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6b7280",
                          fontStyle: "italic",
                          maxWidth: "150px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={payout.adminNote}
                      >
                        {payout.adminNote}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button title="Close" onClick={onClose} bg="grey500" />
      </div>
    </ModalOverlay>
  );
};
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import Helper from "@utils/helper";
import { useNavigate } from "react-router-dom";
import { FiUpload } from "react-icons/fi";
import {
  ActionBtn,
  AmountCell,
  BankDetailsCard,
  DateCell,
  DetailRow,
  HeaderArea,
  InputGroup,
  ModalOverlay,
  ModalSubtitle,
  ModalTitle,
  NoteCell,
  PageWrapper,
  StatusPill,
  StoreInfo,
  Tab,
  TabsRow,
} from "./style";

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case "Approved":
      return <CheckCircle2 size={14} />;
    case "Rejected":
      return <XCircle size={14} />;
    case "Pending":
      return <Clock size={14} />;
    default:
      return null;
  }
};

const ApproveModal = ({
  item,
  onClose,
  onSuccess,
  settings,
}: {
  item: any;
  onClose: () => void;
  onSuccess: () => void;
  settings: any;
}) => {
  const { bucketUrl } = useConfig();
  const [loading, setLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState<any>(null);

  const remainingBalance = item.amount - (item.paidAmount || 0);

  // Get unique bank details (filter by type and remove duplicates)
  const bankDetails = item.bankDetails?.reduce((acc: any[], current: any) => {
    const exists = acc.find(
      (item) =>
        item.type === current.type &&
        JSON.stringify(item.infos) === JSON.stringify(current.infos),
    );
    if (!exists) {
      acc.push(current);
    }
    return acc;
  }, []);

  // Set default selected bank (prefer active one)
  useEffect(() => {
    if (bankDetails?.length > 0 && !selectedBank) {
      const activeBank = bankDetails.find((b: any) => b.isActive);
      setSelectedBank(activeBank || bankDetails[0]);
    }
  }, [bankDetails]);

  const handleSubmit = async (values: any) => {
    if (values.amount > remainingBalance) {
      toast.error(
        `Amount to pay cannot exceed remaining balance (${Helper.formatCurrency(
          remainingBalance,
          settings,
        )})`,
      );
      return;
    }
    if (values.amount <= 0) {
      toast.error("Please enter a valid amount to pay");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("amount", values.amount.toString());
      formData.append("adminNote", values.note);
      formData.append("transactionId", values.txId);

      if (values.receipt?.rawFile) {
        formData.append("receipt", values.receipt.rawFile);
      } else if (values.receipt instanceof File) {
        formData.append("receipt", values.receipt);
      }

      await Withdrawal.approve({
        id: item.id,
        data: formData,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to process payout");
    } finally {
      setLoading(false);
    }
  };

  const renderBankDetails = () => {
    if (!bankDetails?.length) {
      return (
        <BankDetailsCard>
          <span className="title">No Bank Details Available</span>
          <div
            style={{ padding: "20px", textAlign: "center", color: "#6b7280" }}
          >
            User hasn't added any payment methods yet.
          </div>
        </BankDetailsCard>
      );
    }

    return (
      <>
        {bankDetails.length > 1 && (
          <div style={{ marginBottom: "16px" }}>
            <Typography weight={600} size={14} style={{ marginBottom: "8px" }}>
              Select Payment Method
            </Typography>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {bankDetails.map((bank: any, index: number) => (
                <button
                  key={index}
                  onClick={() => setSelectedBank(bank)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "20px",
                    border:
                      selectedBank?.id === bank.id
                        ? "2px solid #3b82f6"
                        : "1px solid #e5e7eb",
                    background:
                      selectedBank?.id === bank.id ? "#eff6ff" : "white",
                    color: selectedBank?.id === bank.id ? "#3b82f6" : "#374151",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {bank.type === "Bank"
                    ? "🏦 Bank"
                    : bank.type === "Khalti"
                      ? "💜 Khalti"
                      : bank.type === "Esewa"
                        ? "💚 eSewa"
                        : "💙 Fonepay"}
                  {bank.isActive && " ✅"}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedBank && (
          <BankDetailsCard key={selectedBank.id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <span className="title" style={{ marginBottom: 0 }}>
                {selectedBank.type === "Bank"
                  ? "🏦 Bank Account Details"
                  : selectedBank.type === "Khalti"
                    ? "💜 Khalti Details"
                    : selectedBank.type === "Esewa"
                      ? "💚 eSewa Details"
                      : "💙 Fonepay Details"}
              </span>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                {selectedBank.isActive && (
                  <span
                    style={{
                      background: "#ecfdf5",
                      padding: "4px 12px",
                      borderRadius: "99px",
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#10b981",
                      border: "1px solid #10b981",
                    }}
                  >
                    Active
                  </span>
                )}
                {selectedBank.type && (
                  <span
                    style={{
                      background: "#f3f4f6",
                      padding: "4px 12px",
                      borderRadius: "99px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#4b5563",
                    }}
                  >
                    {selectedBank.type}
                  </span>
                )}
              </div>
            </div>

            {selectedBank.qrCode && (
              <div className="qr-section" style={{ marginBottom: "20px" }}>
                <Typography
                  weight={500}
                  size={12}
                  style={{ marginBottom: "8px", color: "#6b7280" }}
                >
                  QR Code
                </Typography>
                <div
                  style={{
                    background: "#f9fafb",
                    padding: "20px",
                    borderRadius: "12px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={`${bucketUrl}/${selectedBank.qrCode}`}
                    alt={`${selectedBank.type} QR Code`}
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                    }}
                  />
                </div>
              </div>
            )}

            <div
              className="grid"
              style={{
                gridTemplateColumns:
                  selectedBank.type === "Bank" ? "1fr 1fr" : "1fr",
              }}
            >
              {selectedBank.type === "Bank" ? (
                // Bank details
                <>
                  <div className="item">
                    <span className="label">Bank Name</span>
                    <span className="value">
                      {selectedBank.infos?.bankName || "—"}
                    </span>
                  </div>
                  <div className="item">
                    <span className="label">Account Holder</span>
                    <span className="value">
                      {selectedBank.infos?.name || "—"}
                    </span>
                  </div>
                  <div className="item">
                    <span className="label">Account Number</span>
                    <span className="value">
                      {selectedBank.infos?.number || "—"}
                    </span>
                  </div>
                  <div className="item">
                    <span className="label">Bank Address</span>
                    <span className="value">
                      {selectedBank.infos?.address || "—"}
                    </span>
                  </div>
                </>
              ) : (
                // Digital wallet details (Khalti, eSewa, Fonepay)
                <>
                  <div className="item">
                    <span className="label">{selectedBank.type} Name</span>
                    <span className="value">
                      {selectedBank.infos?.name || "—"}
                    </span>
                  </div>
                  <div className="item">
                    <span className="label">{selectedBank.type} Number</span>
                    <span className="value">
                      {selectedBank.infos?.number || "—"}
                    </span>
                  </div>
                </>
              )}
            </div>
          </BankDetailsCard>
        )}
      </>
    );
  };

  return (
    <ModalOverlay>
      <ModalTitle>Process Withdrawal Payout</ModalTitle>
      <ModalSubtitle>
        Confirm the payment details and upload proof for this portion of the
        withdrawal.
      </ModalSubtitle>

      <div style={{ marginBottom: 20 }}>
        <DetailRow>
          <span className="label">Store</span>
          <span className="value">
            {item.store?.storeName || item.user?.name || "Unknown"}
          </span>
        </DetailRow>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "10px",
            marginTop: "12px",
            background: "#f9fafb",
            padding: "12px",
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                display: "block",
                fontSize: "11px",
                color: "#6b7280",
                marginBottom: "4px",
              }}
            >
              Total Requested
            </span>
            <span style={{ fontWeight: 600, color: "#111827" }}>
              {Helper.formatCurrency(item.amount, settings)}
            </span>
          </div>
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                display: "block",
                fontSize: "11px",
                color: "#6b7280",
                marginBottom: "4px",
              }}
            >
              Already Paid
            </span>
            <span style={{ fontWeight: 600, color: "#10b981" }}>
              {Helper.formatCurrency(item.paidAmount || 0, settings)}
            </span>
          </div>
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                display: "block",
                fontSize: "11px",
                color: "#6b7280",
                marginBottom: "4px",
              }}
            >
              Remaining Due
            </span>
            <span style={{ fontWeight: 600, color: "#ef4444" }}>
              {Helper.formatCurrency(remainingBalance, settings)}
            </span>
          </div>
        </div>
      </div>

      {renderBankDetails()}

      <Formik
        initialValues={{
          note: "",
          txId: "",
          receipt: null,
          amount: remainingBalance,
        }}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            <InputGroup>
              <label>Amount to Pay Now *</label>
              <input
                name="amount"
                type="number"
                value={values.amount}
                onChange={handleChange}
                placeholder={`Max: ${remainingBalance}`}
                max={remainingBalance}
                min={0.01}
                step="0.01"
              />
              {values.amount < remainingBalance && values.amount > 0 && (
                <p
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  Remaining amount of{" "}
                  <strong>
                    {Helper.formatCurrency(
                      remainingBalance - values.amount,
                      settings,
                    )}
                  </strong>{" "}
                  will stay as pending for later payout.
                </p>
              )}
            </InputGroup>

            <InputGroup style={{ marginTop: 20 }}>
              <label>Bank Transaction ID (Optional)</label>
              <input
                name="txId"
                type="text"
                value={values.txId}
                onChange={handleChange}
                placeholder="e.g. TXN-123456789"
              />
            </InputGroup>

            <Upload
              name="receipt"
              label="Payment Receipt Screenshot (Optional)"
              placeholder="Click to upload receipt"
              onChange={(val) => setFieldValue("receipt", val)}
              variant="banner"
              height={160}
            />

            <InputGroup style={{ marginTop: 20 }}>
              <label>Admin Note (Optional)</label>
              <textarea
                name="note"
                rows={2}
                value={values.note}
                onChange={handleChange}
                placeholder="Add a note for this payout..."
              />
            </InputGroup>

            <Row styleRow="justify-content: flex-end; gap: 10px; margin-top: 24px;">
              <Button
                title="Cancel"
                onClick={onClose}
                bg="grey500"
                type="button"
              />
              <Button
                title={loading ? "Processing..." : "Confirm Payout"}
                type="submit"
                disabled={loading}
              />
            </Row>
          </Form>
        )}
      </Formik>
    </ModalOverlay>
  );
};

const RejectModal = ({
  item,
  onClose,
  onSuccess,
  settings,
}: {
  item: any;
  onClose: () => void;
  onSuccess: () => void;
  settings: any;
}) => {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const remainingBalance = item.amount - (item.paidAmount || 0);

  const handleSubmit = async () => {
    if (!note.trim()) {
      toast.error("A reason is required for rejection");
      return;
    }
    setLoading(true);
    try {
      await Withdrawal.reject({
        id: item.id,
        data: { adminNote: note },
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to reject");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalTitle>Reject Withdrawal</ModalTitle>
      <ModalSubtitle>
        This will refund the remaining locked amount (
        {Helper.formatCurrency(remainingBalance, settings)}) back to the user's
        wallet.
      </ModalSubtitle>

      <div style={{ marginBottom: 20 }}>
        <DetailRow>
          <span className="label">Requester</span>
          <span className="value">
            {item.store?.storeName || item.user?.name || "Unknown"}
          </span>
        </DetailRow>
        <DetailRow>
          <span className="label">Remaining Amount</span>
          <span className="value" style={{ color: "#dc2626" }}>
            {Helper.formatCurrency(remainingBalance, settings)}
          </span>
        </DetailRow>
      </div>

      {item.bankDetails && (
        <BankDetailsCard>
          <span className="title">Settlement Bank Details</span>
          <div className="grid">
            <div className="item">
              <span className="label">Account Holder</span>
              <span className="value">
                {item.bankDetails.accountName || "—"}
              </span>
            </div>
            <div className="item">
              <span className="label">Bank Name</span>
              <span className="value">{item.bankDetails.bankName || "—"}</span>
            </div>
          </div>
        </BankDetailsCard>
      )}

      <InputGroup>
        <label>Reason for Rejection *</label>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Explain why this request is being rejected..."
        />
      </InputGroup>

      <Row styleRow="justify-content: flex-end; gap: 10px;">
        <Button title="Cancel" onClick={onClose} bg="grey500" />
        <Button
          title={loading ? "Processing..." : "Reject Withdrawal"}
          onClick={handleSubmit}
          bg="reject"
        />
      </Row>
    </ModalOverlay>
  );
};

import { Formik, Form } from "formik";
import Upload from "@components/fields/upload";
import Typography from "@components/typography";

const ViewReceiptModal = ({
  receipt,
  onClose,
}: {
  receipt: string;
  onClose: () => void;
}) => {
  const { bucketUrl } = useConfig();
  return (
    <ModalOverlay style={{ padding: 0, overflow: "hidden", maxWidth: "90vw" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <ModalTitle style={{ margin: 0, fontSize: "16px" }}>
          Payment Receipt
        </ModalTitle>
        <button
          onClick={onClose}
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          <XCircle size={20} />
        </button>
      </div>
      <div
        style={{
          padding: "24px",
          display: "flex",
          justifyContent: "center",
          background: "#f9fafb",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <img
          src={`${bucketUrl}/${receipt}`}
          alt="Receipt"
          style={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: "8px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          }}
        />
      </div>
      <div
        style={{
          padding: "16px 24px",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          title="Download Receipt"
          onClick={() => window.open(`${bucketUrl}/${receipt}`, "_blank")}
        />
      </div>
    </ModalOverlay>
  );
};

const UploadReceiptModal = ({
  item,
  onClose,
  onSuccess,
  settings,
}: {
  item: any;
  onClose: () => void;
  onSuccess: () => void;
  settings: any;
}) => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    if (!values.receipt) {
      toast.error("Please upload a receipt first");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      if (values.receipt?.rawFile) {
        formData.append("receipt", values.receipt.rawFile);
      } else if (values.receipt instanceof File) {
        formData.append("receipt", values.receipt);
      }

      await Withdrawal.updateReceipt({
        id: item.id,
        data: formData,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to upload receipt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalTitle>Upload Payment Receipt</ModalTitle>
      <ModalSubtitle>
        Upload proof of payment for this withdrawal request.
      </ModalSubtitle>

      <div style={{ marginBottom: 20 }}>
        <DetailRow>
          <span className="label">Store</span>
          <span className="value">{item.store?.storeName || "—"}</span>
        </DetailRow>
        <DetailRow>
          <span className="label">Amount</span>
          <span className="value" style={{ color: "#059669" }}>
            {Helper.formatCurrency(item.amount, settings)}
          </span>
        </DetailRow>
        <DetailRow>
          <span className="label">Transaction ID</span>
          <span className="value">{item.transactionId || "—"}</span>
        </DetailRow>
      </div>

      <Formik initialValues={{ receipt: null }} onSubmit={handleSubmit}>
        {({ setFieldValue }) => (
          <Form>
            <Upload
              name="receipt"
              label="Payment Receipt"
              placeholder="Click to upload receipt"
              onChange={(val) => setFieldValue("receipt", val)}
              variant="banner"
              height={180}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "24px",
              }}
            >
              <Button
                title="Cancel"
                onClick={onClose}
                bg="grey500"
                type="button"
              />
              <Button
                title={loading ? "Uploading..." : "Upload & Save"}
                type="submit"
                disabled={loading}
              />
            </div>
          </Form>
        )}
      </Formik>
    </ModalOverlay>
  );
};

const WithdrawalsPage = () => {
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const { settings } = useGeneralSettings();
  const { bucketUrl } = useConfig();
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [activeRole, setActiveRole] = useState("All"); // "All", "store", "delivery"
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });

  // Date Filter State
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, endDate] = dateRange;
  const [search, setSearch] = useState("");

  const fetchWithdrawals = async (
    page = 1,
    status = activeTab,
    role = activeRole,
  ) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: 10,
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
        search,
      };
      if (status !== "All") {
        params.status = status;
      }
      if (role !== "All") {
        params.role = role;
      }
      const response = await Withdrawal.list({ params });
      setWithdrawals(response.items || []);
      setPagination({
        page: response.page,
        limit: 10,
        totalItems: response.totalItems,
        totalPages: response.totalPages,
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch withdrawals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals(1, activeTab, activeRole);
  }, [activeTab, activeRole, startDate, endDate, search]);

  const handleTabChange = (status: string) => {
    setActiveTab(status);
    setPagination({ ...pagination, page: 1 });
  };

  const handleRoleChange = (role: string) => {
    setActiveRole(role);
    setPagination({ ...pagination, page: 1 });
  };

  const columns = [
    {
      title: "#",
      key: "id",
      render: (item: any) => (
        <span style={{ fontWeight: 600, color: "#6b7280" }}>#{item.id}</span>
      ),
    },
    {
      title: "Store / User",
      key: "user",
      render: (item: any) => (
        <StoreInfo
          onClick={() => {
            navigate(`/admin/users/store/store-lists/view/${item?.user?.id}`);
          }}
        >
          <div className="logo-wrapper">
            {item.store?.coverImg ? (
              <img
                src={`${bucketUrl}/${item.store.coverImg}`}
                alt={item.store?.storeName}
              />
            ) : (
              <span className="placeholder">
                {(item.store?.storeName || item.user?.name || "?")[0]}
              </span>
            )}
          </div>
          <div className="details">
            <span className="name" style={{ textAlign: "left" }}>
              {item.store?.storeName || item.user?.name || "Unknown"}
            </span>
            <div className="owner-info">
              {item.user?.name && <span>{item.user.name}</span>}
              {item.user?.role && (
                <span
                  style={{
                    color: "#6366f1",
                    textTransform: "uppercase",
                    fontSize: "10px",
                    marginLeft: "4px",
                  }}
                >
                  • {item.user.role}
                </span>
              )}
            </div>
            {item.user?.phoneNo && (
              <span className="phone">{item.user.phoneNo}</span>
            )}
          </div>
        </StoreInfo>
      ),
    },
    {
      title: "Amount (Paid / Total)",
      key: "amount",
      style: { textAlign: "center" },
      render: (item: any) => (
        <AmountCell style={{ fontSize: "14px", textAlign: "center" }}>
          <div>
            <span style={{ color: "#10b981", fontWeight: 700 }}>
              {Helper.formatCurrency(item.paidAmount || 0, settings)}
            </span>
            <span style={{ color: "#9ca3af", margin: "0 4px" }}>/</span>
            <span style={{ color: "#6b7280" }}>
              {Helper.formatCurrency(item.amount, settings)}
            </span>
          </div>
          {item.payouts && item.payouts.length > 0 && (
            <div
              onClick={() =>
                showModal(
                  <PayoutHistoryModal
                    item={item}
                    onClose={hideModal}
                    settings={settings}
                  />,
                )
              }
              style={{
                fontSize: "11px",
                color: "#6366f1",
                cursor: "pointer",
                marginTop: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              <History size={12} />
              View History
            </div>
          )}
        </AmountCell>
      ),
    },
    {
      title: "Requested On",
      key: "createdAt",
      style: { textAlign: "center" },
      render: (item: any) => (
        <DateCell style={{ textAlign: "center" }}>
          {new Date(item.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </DateCell>
      ),
    },
    {
      title: "Status",
      key: "status",
      style: { textAlign: "center" },
      render: (item: any) => {
        const isPartiallyPaid =
          item.status === "Pending" && (item.paidAmount || 0) > 0;
        const isPartiallyPaidAndRejected =
          item.status === "Rejected" && (item.paidAmount || 0) > 0;

        let displayStatus = item.status;
        let pillStatus = item.status;

        if (isPartiallyPaid) {
          displayStatus = "Partially Paid";
          pillStatus = "Approved";
        } else if (isPartiallyPaidAndRejected) {
          displayStatus = "Partially Paid & Rejected";
          pillStatus = "Rejected";
        }

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <StatusPill status={pillStatus}>
              <StatusIcon status={pillStatus} />
              {displayStatus}
            </StatusPill>
          </div>
        );
      },
    },
    {
      title: "Note",
      key: "adminNote",
      render: (item: any) =>
        item.adminNote ? (
          <NoteCell title={item.adminNote}>{item.adminNote}</NoteCell>
        ) : (
          <NoteCell>—</NoteCell>
        ),
    },
    {
      title: "Receipt",
      key: "receipt",
      style: { textAlign: "center" },
      render: (item: any) =>
        item.receipt ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              onClick={() =>
                showModal(
                  <ViewReceiptModal
                    receipt={item.receipt}
                    onClose={hideModal}
                  />,
                )
              }
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                overflow: "hidden",
                border: "1px solid #e5e7eb",
                display: "block",
                cursor: "pointer",
              }}
            >
              <img
                src={`${bucketUrl}/${item.receipt}`}
                alt="Receipt"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </div>
        ) : (
          <NoteCell style={{ textAlign: "center" }}>—</NoteCell>
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (item: any) => {
        if (item.status === "Pending") {
          return (
            <div style={{ display: "flex" }}>
              <ActionBtn
                variant="approve"
                onClick={() =>
                  showModal(
                    <ApproveModal
                      item={item}
                      onClose={hideModal}
                      settings={settings}
                      onSuccess={() =>
                        fetchWithdrawals(pagination.page, activeTab)
                      }
                    />,
                  )
                }
              >
                <CheckCircle2 size={13} />
                Approve
              </ActionBtn>
              <ActionBtn
                variant="reject"
                onClick={() =>
                  showModal(
                    <RejectModal
                      item={item}
                      onClose={hideModal}
                      settings={settings}
                      onSuccess={() =>
                        fetchWithdrawals(pagination.page, activeTab)
                      }
                    />,
                  )
                }
              >
                <XCircle size={13} />
                Reject
              </ActionBtn>
            </div>
          );
        }

        if (item.status === "Approved" && !item.receipt) {
          return (
            <ActionBtn
              variant="approve"
              style={{
                background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
              }}
              onClick={() =>
                showModal(
                  <UploadReceiptModal
                    item={item}
                    onClose={hideModal}
                    onSuccess={() =>
                      fetchWithdrawals(pagination.page, activeTab)
                    }
                    settings={settings}
                  />,
                )
              }
            >
              <FiUpload size={14} />
              Upload Receipt
            </ActionBtn>
          );
        }

        if (item.status === "Approved" && item.receipt) {
          return (
            <ActionBtn
              variant="approve"
              style={{
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
              }}
              onClick={() =>
                showModal(
                  <ViewReceiptModal
                    receipt={item.receipt}
                    onClose={hideModal}
                  />,
                )
              }
            >
              <Eye size={14} />
              View Receipt
            </ActionBtn>
          );
        }

        return <NoteCell>—</NoteCell>;
      },
    },
  ];

  return (
    <PageWrapper>
      <HeaderArea>
        <Title textone="Withdrawal" texttwo="Requests" />
        <div style={{}}>
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
            }}
            isClearable={true}
            placeholderText="Filter by date range"
            className="custom-datepicker-input"
          />
        </div>
        <div style={{ marginRight: 20 }}>
          <TabsRow style={{ marginBottom: 0 }}>
            {["All", "store", "delivery"].map((role) => (
              <Tab
                key={role}
                active={activeRole === role}
                onClick={() => handleRoleChange(role)}
                style={{ textTransform: "capitalize" }}
              >
                {role === "All" ? "All Users" : role}
              </Tab>
            ))}
          </TabsRow>
        </div>
      </HeaderArea>

      <WithdrawalStats
        filters={{
          startDate: startDate ? startDate.toISOString() : undefined,
          endDate: endDate ? endDate.toISOString() : undefined,
          role: activeRole !== "All" ? activeRole : undefined,
        }}
      />

      <TabsRow>
        {["All", "Pending", "Approved", "Rejected"].map((status) => (
          <Tab
            key={status}
            active={activeTab === status}
            onClick={() => handleTabChange(status)}
          >
            {status}
          </Tab>
        ))}
      </TabsRow>

      <Table
        hasSearchbar={true}
        onSearchChange={(val) => setSearch(val)}
        text="Search by Name, Phone, Email..."
        columns={columns}
        data={withdrawals}
        loading={loading}
        totalItems={pagination.totalItems}
        itemsPerPage={pagination.limit}
        totalPages={pagination.totalPages}
        pageControlled={pagination.page}
        onPageChange={(page) => fetchWithdrawals(page, activeTab, activeRole)}
      />
    </PageWrapper>
  );
};

export default WithdrawalsPage;
